import Checkbox from '../../components/core/checkbox'
import Customer from '../../utils/customer'
import {useDispatch, useSelector} from 'react-redux'
import {setSelectedResource, toggleResourcesStatus} from '../../stores/resources'
import {ActionButton, IntegrationButton, ResourceName, VisitButton} from './index'
import {locationManager} from '../../utils/locations'
import Tooltip from '../../components/core/tooltip'
import {ShortDownIcon, MinusIcon, ShortUpIcon, TargetIcon, UrlIcon} from '../../icons'
import classNames from 'classnames'
import types from '../../data/monitoringTypes.json'
import Badge from '../../components/core/badge'
import Spinner from '../../components/core/spinner'
import {useNavigate} from 'react-router-dom'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {DateFormats, formatDate} from '../../utils/date'

const ResourceUrl = ({resource}) => {
    const isHttp = resource?.type === types.http.value
    const url = isHttp ? resource?.url : resource?.host
    return (
        <Tooltip content={url}>
            {!isHttp ? <span className="target">
                <TargetIcon />
            </span> : <a href={url} target="_blank">
                <span className="target">
                    <UrlIcon />
                </span>
            </a>}
        </Tooltip>
    )
}

const ListItem = ({item, responseTimeData, itemCount, setClickedResource, isDarkMode, handleModal}) => {
    const {resource_id: id} = item
    const  translations = useNamespaceTranslations('resources')
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const selectedResources = useSelector(state => state.resources.selectedResources)
    const responseDataIsFetching = useSelector(state => state.monitoring.responseDataIsFetching)

    const handleChange = () => {
        const newData = [...selectedResources]
        if (newData.includes(id)) {
            dispatch(setSelectedResource(newData.filter(resourceId => resourceId !== id)))
            return
        }
        newData.push(id)
        dispatch(setSelectedResource(newData))
    }

    const getResourceType = () => {
        if (item?.type === types.icmp.value) {
            return <Badge color={types[item?.type]?.color}>{types.icmp.lowercase_label}</Badge>
        }
        return <Badge color={types[item?.type]?.color}>{item?.type}</Badge>
    }

    const getRegion = state => {
        const newData = item?.region_details?.filter(data => data.state === state)
        return item?.enabled && newData.length ? (
            <div className="resource-regions">
                {newData.map(data => (
                    <span className="resource-region" key={data?.region}>
                        <Tooltip content={locationManager.allRegions[data?.region]?.location}>
                            {locationManager.allRegions[data?.region]?.icon}
                        </Tooltip>
                    </span>
                ))}
            </div>
        ) : <MinusIcon stroke="grey"/>
    }

    const playMonitoring = resourceId => {
        const id = typeof resourceId === 'string' ? resourceId : null
        dispatch(toggleResourcesStatus([id || item?.resource_id], {enabled: true}))
    }

    const pauseMonitoring = resourceId => {
        const id = typeof resourceId === 'string' ? resourceId : null
        dispatch(toggleResourcesStatus([id || item?.resource_id], {enabled: false}))
    }

    return (
        <div className={classNames({
            'list-item': true,
            selected: selectedResources.includes(id),
            multi: itemCount > 1
        })}>
            <div className="left-content">
                {Customer.isOwnTeam() && (
                    <Checkbox onChange={handleChange} type="checkbox" id={id} name={id} value={id}
                        checked={selectedResources.includes(id)}/>
                )}
                <div className="header">
                    <ResourceName resource={item} isListView/>
                    <span className="d-flex gap-half">
                        {getResourceType()}
                        {item?.is_advanced && <span><Badge className="lowercase" color="primary">{translations.advanced_label}</Badge></span>}
                        <ResourceUrl resource={item}/>
                    </span>
                </div>
            </div>
            <div className="right-content">
                <div className="content-info-box">
                    <span className="title">
                        <ShortDownIcon/>
                        {translations.down}
                    </span>
                    {getRegion(0)}
                </div>
                <div className="content-info-box">
                    <span className="title">
                        <ShortUpIcon/>
                        {translations.up}
                    </span>
                    {getRegion(1)}
                </div>
                <div className="content-info-box response-time">
                    <span>
                        <span className="title">
                            {translations.response_time}
                        </span>
                        {responseDataIsFetching ? <Spinner /> :
                            <p>
                                {responseTimeData?.avg_duration ? `${responseTimeData?.avg_duration} ms (${translations.average})` : <MinusIcon stroke="grey"/>}
                            </p>
                        }
                    </span>
                    {!responseDataIsFetching && (
                        <span className="statistics">
                            {responseTimeData?.statistics?.length ? responseTimeData?.statistics?.map((statistic, index) => {
                                const height = `${statistic?.duration / responseTimeData?.max_duration * 100}%`
                                return (
                                    <span key={index}>
                                        <Tooltip content={<span dangerouslySetInnerHTML={{__html:
                                            `
                                                <p class='text-center'>${statistic?.duration} ms (${translations.hourly_average})</p>
                                                <span>${formatDate(statistic?.checked_at, DateFormats.TIME_SLASH_DMY)} (${locationManager.allRegions[item?.regions?.[0]]?.location})</span>
                                                ${!statistic?.state ? `
                                                    <p class='mt-1'>${translations.incident_detected}</p>
                                                ` : ''}
                                            `}}/>
                                        }>
                                            <span className="bar">
                                                <span className={classNames({
                                                    'cover': true,
                                                    'up': statistic?.state,
                                                    'down': !statistic?.state
                                                })}
                                                style={{height: height}}
                                                />
                                            </span>
                                        </Tooltip>
                                    </span>
                                )
                            }) : ''}
                        </span>
                    )}
                </div>
                <div className="content-info-box actions">
                    <span className="d-flex align-items-center justify-content-flex-end gap-half">
                        <VisitButton
                            id={id}
                            isDarkMode={isDarkMode}
                            handleModal={handleModal}
                            viewStatisticsText={translations.view_statistics}
                            incidentsText={translations.incidents}/>
                        {Customer.isOwnTeam() && <>
                            <IntegrationButton id={id} isDarkMode={isDarkMode} navigate={navigate}/>
                            <ActionButton data={item} playMonitoring={playMonitoring} pauseMonitoring={pauseMonitoring}
                                setClickedResource={setClickedResource} isDarkMode={isDarkMode}/>
                        </>}
                    </span>
                </div>
            </div>
        </div>
    )
}

export default ListItem
